import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getStandings, getMeta } from "../lib/api";

const streakVal = (s) => {
  if (!s || s.length < 2) return 0;
  const n = parseInt(s.slice(1), 10) || 0;
  return s[0] === "W" ? n : -n;
};

const SORTS = {
  winPct: (t) => t.winPct,
  wins: (t) => t.wins,
  pointsFor: (t) => t.pointsFor,
  pointsAgainst: (t) => -t.pointsAgainst,
  streak: (t) => streakVal(t.streak),
};

const DivisionTable = ({ title, teams, sortKey }) => {
  const sorted = [...teams].sort((a, b) => SORTS[sortKey](b) - SORTS[sortKey](a) || b.pointsFor - a.pointsFor);
  return (
  <div data-testid={`division-${title.replace(/\s/g, "-").toLowerCase()}`} className="border border-border bg-card">
    <div className="px-4 py-2 bg-secondary border-b border-border">
      <h3 className="font-display text-xl tracking-wide">{title}</h3>
    </div>
    <table className="w-full text-sm">
      <thead>
        <tr className="label-caps border-b border-border">
          <th className="text-left font-normal px-4 py-2">Team</th>
          <th className="text-right font-normal px-2 py-2">W</th>
          <th className="text-right font-normal px-2 py-2">L</th>
          <th className="text-right font-normal px-2 py-2 hidden sm:table-cell">PCT</th>
          <th className="text-right font-normal px-2 py-2 hidden md:table-cell">PF</th>
          <th className="text-right font-normal px-2 py-2 hidden md:table-cell">PA</th>
          <th className="text-right font-normal px-4 py-2">STRK</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((t, i) => (
          <tr
            key={t.id}
            className="border-b border-border/50 last:border-0 hover:bg-white/5 transition-colors duration-150"
          >
            <td className="px-4 py-2.5">
              <Link
                to={`/team/${t.id}`}
                data-testid={`standings-team-${t.id}`}
                className="flex items-center gap-2.5 hover:text-primary transition-colors duration-150"
              >
                {i === 0 && <span className="w-1 h-5 bg-primary" />}
                <img src={t.logo} alt={t.abbr} className="w-6 h-6 object-contain" />
                <span className="font-semibold">{t.fullName}</span>
              </Link>
            </td>
            <td className="text-right px-2 tabular-nums font-semibold">{t.wins}</td>
            <td className="text-right px-2 tabular-nums text-muted-foreground">{t.losses}</td>
            <td className="text-right px-2 tabular-nums hidden sm:table-cell">{t.winPct.toFixed(3)}</td>
            <td className="text-right px-2 tabular-nums hidden md:table-cell">{t.pointsFor}</td>
            <td className="text-right px-2 tabular-nums hidden md:table-cell">{t.pointsAgainst}</td>
            <td className="text-right px-4 tabular-nums font-semibold text-primary">{t.streak}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

export default function Standings() {
  const [conf, setConf] = useState("AFC");
  const [sortKey, setSortKey] = useState("winPct");
  const { data: standings } = useQuery({ queryKey: ["standings"], queryFn: getStandings });
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });

  const sortOptions = [
    { key: "winPct", label: "Win %" },
    { key: "wins", label: "Wins" },
    { key: "pointsFor", label: "Points For" },
    { key: "pointsAgainst", label: "Points Against" },
    { key: "streak", label: "Streak" },
  ];

  return (
    <div data-testid="standings-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-border pb-2">
        <h1 className="font-display text-5xl">
          STANDINGS{" "}
          <span data-testid="standings-season-label" className="label-caps align-middle ml-2">{meta?.statsSeason}</span>
        </h1>
        <div className="flex border border-border">
          {["AFC", "NFC"].map((c) => (
            <button
              key={c}
              data-testid={`conf-btn-${c}`}
              onClick={() => setConf(c)}
              className={`px-5 py-2 text-sm font-bold uppercase tracking-wider transition-colors duration-150 ${
                conf === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div data-testid="standings-sort" className="flex flex-wrap items-center gap-2">
        <span className="label-caps mr-1">Sort by</span>
        {sortOptions.map((o) => (
          <button
            key={o.key}
            data-testid={`sort-${o.key}`}
            onClick={() => setSortKey(o.key)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors duration-150 ${
              sortKey === o.key
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:text-foreground hover:border-primary"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {standings && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {Object.entries(standings[conf]).map(([div, teams]) => (
            <DivisionTable key={div} title={`${conf} ${div}`} teams={teams} sortKey={sortKey} />
          ))}
        </div>
      )}
    </div>
  );
}
